import React, { useEffect, useState, useCallback, useRef } from 'react';
import TopNav from '../../components/layout/TopNav';
import type { ScoringStats, TeamStats } from '../../types';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import TeamPerformanceTab from './tabs/TeamPerformanceTab';
import SignalTrendsTab from './tabs/SignalTrendsTab';
import ComplianceAuditTab from './tabs/ComplianceAuditTab';
import CoachingStatsTab from './tabs/CoachingStatsTab';
import OutcomesTab from './tabs/OutcomesTab';

interface TeamMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
}

type AnalyticsTab = 'performance' | 'signal-trends' | 'compliance-audit' | 'coaching-stats' | 'outcomes';

const TABS: Array<{ id: AnalyticsTab; label: string; icon: string; adminOnly?: boolean }> = [
  { id: 'performance', label: 'Team Performance', icon: 'auto_graph' },
  { id: 'signal-trends', label: 'Signal Trends', icon: 'show_chart' },
  { id: 'compliance-audit', label: 'Compliance Audit', icon: 'verified_user', adminOnly: true },
  { id: 'coaching-stats', label: 'Coaching Stats', icon: 'school', adminOnly: true },
  { id: 'outcomes', label: 'Outcomes', icon: 'insights', adminOnly: true },
];

const AnalyticsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'TENANT_ADMIN';

  const [activeTab, setActiveTab] = useState<AnalyticsTab>('performance');

  const [stats, setStats] = useState<ScoringStats | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<
    Array<{ userId: string; name: string; avgScore: number; videoCount: number; avgGrade: string }>
  >([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (userId?: string) => {
    const params = userId ? `?userId=${userId}` : '';
    const [statsRes, teamRes] = await Promise.all([
      api.get(`/scoring/stats${params}`),
      api.get('/scoring/team-stats'),
    ]);
    setStats(statsRes.data.data as ScoringStats);
    setTeamStats(teamRes.data.data as TeamStats);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const promises: Promise<unknown>[] = [fetchStats()];

        if (isAdmin) {
          promises.push(
            api
              .get('/users?limit=1000')
              .then((res) => {
                const members = (res.data.data ?? []) as TeamMember[];
                setTeamMembers(members.filter((m) => m.role === 'SALES_REP'));
              })
              .catch(() => {}),
            api
              .get('/reports/leaderboard')
              .then((res) => {
                setLeaderboard(res.data.data ?? []);
              })
              .catch(() => {}),
          );
        }

        await Promise.all(promises);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isAdmin, fetchStats]);

  const handleUserFilter = async (userId: string) => {
    setSelectedUserId(userId);
    setLoading(true);
    try {
      await fetchStats(userId || undefined);
    } finally {
      setLoading(false);
    }
  };

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);

  const tabBarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll]);

  const scrollTabs = (dir: 'left' | 'right') => {
    tabBarRef.current?.scrollBy({ left: dir === 'left' ? -160 : 160, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col transition-colors duration-300">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
              Performance Intelligence
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              {isAdmin
                ? 'Team analytics, signal trends, compliance audit, and coaching insights'
                : 'Personalized performance insights, scoring trends, and compliance activity'}
            </p>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="relative flex items-center mb-8 border-b border-slate-200 dark:border-slate-800">
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollTabs('left')}
              className="flex-shrink-0 flex items-center justify-center w-7 h-10 text-slate-400 hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
          )}
          <div
            ref={tabBarRef}
            className="flex items-center gap-1 overflow-x-auto scrollbar-none flex-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 h-10 px-4 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${
                  activeTab === t.id
                    ? 'text-primary border-primary'
                    : 'text-slate-400 dark:text-slate-600 border-transparent hover:text-slate-600 dark:hover:text-slate-400'
                }`}
              >
                <span className="material-symbols-outlined text-[16px]">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollTabs('right')}
              className="flex-shrink-0 flex items-center justify-center w-7 h-10 text-slate-400 hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          )}
        </div>

        {/* Tab content */}
        {activeTab === 'performance' && (
          <TeamPerformanceTab
            stats={stats}
            teamStats={teamStats}
            leaderboard={leaderboard}
            teamMembers={teamMembers}
            selectedUserId={selectedUserId}
            loading={loading}
            isAdmin={isAdmin}
            onSelectUser={handleUserFilter}
          />
        )}

        {activeTab === 'signal-trends' && (
          <SignalTrendsTab isAdmin={isAdmin} teamMembers={teamMembers} />
        )}

        {activeTab === 'compliance-audit' && isAdmin && <ComplianceAuditTab />}

        {activeTab === 'coaching-stats' && isAdmin && <CoachingStatsTab />}

        {activeTab === 'outcomes' && isAdmin && <OutcomesTab />}
      </main>
    </div>
  );
};

export default AnalyticsPage;
